import { useCallback, useState, useMemo } from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import { FormField, FormSection, useForm, useFormIsValidAlert } from '@hilma/netina2all';
import { I18n } from '../types';

interface GridifySectionProps {
	keepOpen?: boolean;
	title?: string;
	fields?: Array<string | FormField<I18n>[] | FormSection<I18n>>;
	invalidDrivers?: boolean;
}

const GridifySection: React.FC<GridifySectionProps> = ({ children, keepOpen = false, title, fields, invalidDrivers = false }) => {
	const [open, setOpen] = useState(false);

	const form = useForm();
	const classes = useStyles();

	useFormIsValidAlert(null, () => {

		const fieldIdsInSection = fields?.map(fieldUnit => {

			if (typeof fieldUnit === 'boolean') return '';

			if (typeof fieldUnit === 'string') return fieldUnit;

			if (Array.isArray(fieldUnit)) return fieldUnit.map(unit => unit.id.toString());

			return fieldUnit.data.map(unit => unit.id.toString());

		}).flat();

		const erroredFields = Object.keys(form.errors);

		const sectionContainsErr = (fieldIdsInSection?.map(field =>
			(erroredFields.includes(field)))
			.some(Boolean));

		if (sectionContainsErr || invalidDrivers) setOpen(true);

	});


	const isNotMobile = useMediaQuery('(min-width: 801px)');

	const openCollapse = useMemo(() => isNotMobile || open, [open, isNotMobile]);

	const toggleSection = useCallback(() => {

		setOpen(prev => !isNotMobile && !prev);

	}, [isNotMobile]);


	return (
		<List className={classes.list}>
			{!isNotMobile && (
				<ListItem
					button
					color="primary"
					onClick={toggleSection}
					className={clsx(classes.listItem, !openCollapse && classes.listSection)}
				>
					<div className={classes.sectionTitle}>{title}</div>
					{!keepOpen && (
						<ListItemSecondaryAction className={classes.iconPosition}>
							<IconButton onClick={toggleSection} color="primary">
								{openCollapse ? (
									<ExpandLess fontSize="large" />
								) : (
									<ExpandMore fontSize="large" />
								)}
							</IconButton>
						</ListItemSecondaryAction>
					)}
				</ListItem>
			)}
			<Collapse in={openCollapse || keepOpen} className={classes.collapseContent}>
				<div className={classes.collapseContentPadding}>
					{children}
				</div>
			</Collapse>
		</List>
	);
}

export default GridifySection;

export const useStyles = makeStyles(theme => ({
	list: {
		padding: 0
	},

	listItem: {
		color: theme.palette.primary.main,
		paddingTop: 16,
		paddingBottom: 16,
		paddingLeft: 0
	},

	listSection: {
		boxShadow: '0 1px 0 #e6e6e6'
	},

	sectionTitle: {
		fontFamily: 'Assistant-Bold',
		fontSize: 22,
		color: theme.palette.primary.main,
	},

	iconPosition: {
		display: 'flex',
		flexDirection: 'row',
		justifyItems: 'center',
		alignItems: 'center'
	},

	collapseContent: {
		[theme.breakpoints.down(800)]: {
			boxShadow: '0 1px 0 #e6e6e6'
		}
	},

	collapseContentPadding: {
		[theme.breakpoints.down(800)]: {
			paddingTop: 20,
		}
	}
}));